import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import type { ManageReleaseFormType } from '@/types';

import useAddReleaseNote from '@/hooks/use-add-release-note';
import { fetchCheckReleaseVersion } from '@/hooks/use-check-release-version';
import useGetReleaseNote from '@/hooks/use-get-release-note';
import useUpdateReleaseNote from '@/hooks/use-update-release-note';

const useManageReleaseForm = () => {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id');

  const form = useForm<ManageReleaseFormType>({
    mode: 'onChange',
    defaultValues: {
      version: '',
      releaseDate: undefined,
      isPublished: false,
      description: '',
    },
  });

  const { dirtyFields } = form.formState;

  const { data: releaseNoteData, isLoading: isFetching, isError } = useGetReleaseNote(id as string);
  const { mutate: updateReleaseNote, isPending: isUpdating } = useUpdateReleaseNote(id as string);
  const { mutate: addReleaseNote, isPending: isAdding } = useAddReleaseNote();

  const isLoading = isFetching || isAdding || isUpdating;

  useEffect(() => {
    if (releaseNoteData) {
      const { version, releaseDate, isPublished, description } = releaseNoteData;

      form.reset({
        version: version,
        releaseDate: releaseDate ? parseISO(releaseDate) : undefined,
        isPublished: isPublished,
        description: description,
      });
    }
  }, [releaseNoteData, form]);

  const handleClose = () => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete('id');
    const queryString = newSearchParams.toString();

    navigate({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : '',
    });
  };

  const executeSubmit = () => {
    const { version, releaseDate, description, isPublished } = form.getValues();

    if (!releaseDate) {
      return;
    }

    const payload = {
      version,
      releaseDate: format(releaseDate, 'yyyy-MM-dd'),
      description,
      isPublished,
    };

    const mutationOptions = {
      onSuccess: () => {
        form.reset();
        handleClose();
        setIsWarningModalOpen(false);
      },
    };

    if (id) {
      updateReleaseNote(payload, mutationOptions);
    } else {
      addReleaseNote(payload, mutationOptions);
    }
  };

  const onSubmit = async (data: ManageReleaseFormType) => {
    const { version, releaseDate } = data;

    if (!releaseDate) {
      return;
    }

    try {
      if (dirtyFields.version) {
        const checkResult = await queryClient.fetchQuery({
          queryKey: ['check-release-note-version', version],
          queryFn: () => fetchCheckReleaseVersion(version),
        });

        if (checkResult?.exists) {
          setIsWarningModalOpen(true);

          return;
        }
      }

      executeSubmit();
    } catch {
      return;
    }
  };

  return {
    form,
    id,
    isLoading,
    isError,
    isWarningModalOpen,
    setIsWarningModalOpen,
    onSubmit,
    executeSubmit,
    handleClose,
  };
};
export default useManageReleaseForm;
